import { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { API_BASE } from "../../../config";
import {
    Warning,
    Error,
    Explore,
    ArrowheadUp,
    ArrowheadDown,
    Check,
    Chat2,
} from "../../icons";

const TYPE_META = {
    alert: {
        Icon: Error,
        color: "#EF4444",
        label: "Predictive",
    },
    warning: {
        Icon: Warning,
        color: "#F59E0B",
        label: "Optimization",
    },
    info: {
        Icon: Explore,
        color: "#3AA6FF",
        label: "General AI",
    },
    trend_up: {
        Icon: ArrowheadUp,
        color: "#10B981",
        label: "Forecast",
    },
    trend_down: {
        Icon: ArrowheadDown,
        color: "#F59E0B",
        label: "Forecast",
    },
    success: {
        Icon: Check,
        color: "#10B981",
        label: "Success",
    },
};

const FALLBACK_META = {
    Icon: Chat2,
    color: "#6F3AFF",
    label: "Insight",
};

function normalizeInsights(payload) {
    // Only the real "insights" array should be shown in this panel.
    // Alerts and next-best-actions are separate backend sections and should not
    // increase the "x of y insights" count.
    const raw = payload?.insights
        ? payload
        : payload?.data?.insights
            ? payload.data
            : payload ?? {};

    const primaryInsights = Array.isArray(raw.insights) ? raw.insights : [];

    return primaryInsights
        .map((item, index) => ({
            id: `${item.title || "insight"}-${index}`,
            type: String(item.type || item.severity || "info").toLowerCase(),
            title: item.title || `Insight ${index + 1}`,
            description: item.description || item.text || "",
            priority: Number(item.priority ?? index + 1),
            badge: item.badge || item.category || "",
            delta: item.delta || item.change || "",
        }))
        .sort((a, b) => a.priority - b.priority);
}

function getMeta(type) {
    return TYPE_META[type] || FALLBACK_META;
}

function toBubbleBg(hexColor) {
    const red = parseInt(hexColor.slice(1, 3), 16);
    const green = parseInt(hexColor.slice(3, 5), 16);
    const blue = parseInt(hexColor.slice(5, 7), 16);

    return `rgba(${red}, ${green}, ${blue}, 0.22)`;
}

export default function AiInsightsPanel() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [insights, setInsights] = useState([]);
    const [selectedId, setSelectedId] = useState("");

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                setLoading(true);
                setError("");

                const response = await fetchWithAuth(
                    `${API_BASE}/ainstein/in/dashboard_insights`
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const payload = await response.json();
                const normalized = normalizeInsights(payload);

                if (!cancelled) {
                    setInsights(normalized);
                    setSelectedId(normalized[0]?.id || "");
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Failed to load AI insights");
                    setInsights([]);
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, []);

    const visibleInsights = useMemo(() => insights.slice(0, 5), [insights]);

    const selectedInsight = useMemo(() => {
        return (
            visibleInsights.find((item) => item.id === selectedId) ||
            visibleInsights[0] ||
            null
        );
    }, [visibleInsights, selectedId]);

    const selectedInsightIndex = selectedInsight
        ? visibleInsights.findIndex((item) => item.id === selectedInsight.id)
        : -1;

    return (
        <section className="rd-visual-card rd-ai-panel">
            <div className="rd-ai-panel-header">
                <div className="rd-visual-title-row">
                    <span className="rd-ai-header-icon">
                        <Chat2 width={16} height={16} />
                    </span>
                    <h3 className="rd-visual-card-title">AI Insights</h3>
                    <span className="rd-live-badge">Dynamic</span>
                </div>

                <span className="rd-ai-updated">Model last updated recently</span>
            </div>

            {loading ? (
                <div className="remediation-dashboard-loading">
                    <span className="remediation-dashboard-loading-spinner" />
                    <span>Analysing data...</span>
                </div>
            ) : error ? (
                <div className="remediation-dashboard-empty-state">{error}</div>
            ) : visibleInsights.length === 0 ? (
                <div className="remediation-dashboard-empty-state">
                    No AI insights available.
                </div>
            ) : (
                <div className="rd-ai-panel-body">
                    <div className="rd-ai-list">
                        {visibleInsights.map((item) => {
                            const meta = getMeta(item.type);
                            const ItemIcon = meta.Icon;
                            const isActive = item.id === selectedInsight?.id;

                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`rd-ai-list-item ${isActive ? "is-active" : ""}`}
                                    onClick={() => setSelectedId(item.id)}
                                >
                                    <span
                                        className="rd-ai-list-icon"
                                        style={{
                                            background: toBubbleBg(meta.color),
                                            color: meta.color,
                                        }}
                                    >
                                        <ItemIcon width={16} height={16} />
                                    </span>

                                    <span className="rd-ai-list-text">
                                        <span className="rd-ai-list-title">{item.title}</span>
                                        <span className="rd-ai-list-label">{meta.label}</span>
                                    </span>

                                    <span className="rd-ai-list-chevron">›</span>
                                </button>
                            );
                        })}
                    </div>

                    {selectedInsight && (
                        <div className="rd-ai-detail">
                            <div className="rd-ai-detail-top">
                                <span className="rd-ai-detail-badge">
                                    {selectedInsight.badge ||
                                        getMeta(selectedInsight.type).label ||
                                        "Insight"}
                                </span>

                                {selectedInsight.delta && (
                                    <span className="rd-ai-detail-delta">
                                        {selectedInsight.delta}
                                    </span>
                                )}
                            </div>

                            <h4 className="rd-ai-detail-title">{selectedInsight.title}</h4>

                            <p className="rd-ai-detail-description">
                                {selectedInsight.description || "No insight details available."}
                            </p>

                            <div className="rd-ai-detail-footer">
                                <span className="rd-ai-dots">
                                    {visibleInsights.map((item) => (
                                        <span
                                            key={item.id}
                                            className={
                                                item.id === selectedInsight.id ? "is-active" : ""
                                            }
                                        />
                                    ))}
                                </span>

                                <span>
                                    {selectedInsightIndex + 1} of {visibleInsights.length} insights
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}